// Landing page sections below the search (static content, prerendered for search engines).
import { useEffect, useRef } from 'preact/hooks';
import { t } from '../i18n';
import { track } from '../report/analytics';
import { leadEnabled, WaitlistForm } from '../report/LeadForm';
import { gradientCss, RAMP_ANNUAL } from '../report/maps/render';
import demo from './demo.json';

interface Img { src: string; w: number; h: number }
interface Demo {
  href: string; title: string;
  facts: { v: string; unit: string; label: string }[];
  findings: string[];
  images: { map: Img; three: Img };
  more: { href: string; label: string; note: string }[];
}
const D = demo as Demo;

const ICONS: string[] = [
  // map grid, clock, sun path, roof, cube, share
  'M3 3h18v18H3zM3 9h18M3 15h18M9 3v18M15 3v18',
  'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM12 7v5l3 3',
  'M2 19c3-9 17-9 20 0M12 5v2M5 8l1.5 1.5M19 8l-1.5 1.5M2 19h20',
  'M3 12l9-7 9 7M5 10v10h14V10M9 20v-6h6v6',
  'M12 2l9 5v10l-9 5-9-5V7zM12 12l9-5M12 12v10M12 12L3 7',
  'M18 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM6 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM18 22a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM8.6 13.5l6.8 4M15.4 6.5l-6.8 4',
];
// audience: house with sun, drafting triangle and pencil, terrain plot with a corner marker
const AUD_ICONS: string[] = [
  'M3 21h18M5 21V11l7-5 7 5v10M10 21v-5h4v5M18 3v1.5M21 6h-1.5M20.1 3.9l-1 1',
  'M3 21L21 3v18zM8.5 15.5h4v-4M3 21h18M17 9v8',
  'M2 19l6-8 4 5 3-3 7 6zM2 21h20M16 8V3l4 1.5L16 6',
];

/** Decorative sun-path arcs behind the hero sheet: summer solstice, equinox, winter solstice. */
function HeroArcs() {
  return (
    <svg class="hero-arcs" viewBox="0 0 600 340" aria-hidden="true">
      <path class="a1" d="M20 330 C 120 -40, 480 -40, 580 330" />
      <path class="a2" d="M90 330 C 170 60, 430 60, 510 330" />
      <circle cx="402" cy="77" r="8" />
      <text x="300" y="42" text-anchor="middle">21. 6.</text>
      <text x="300" y="116" text-anchor="middle">21. 3.</text>
    </svg>
  );
}

// the hero loop (scripts/hero-video.ts): 21. 6. from 04:30 to 21:30 over the clip; the poster is 13:00
const HERO_DAY = [4 * 60 + 30, 21 * 60 + 30];
const HERO_POSTER = 13 * 60;
const hhmm = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

/** Time-lapse of the demo parcel's 3D view. Plays only on screen and never for prefers-reduced-motion (poster stays). */
function HeroVideo() {
  const ref = useRef<HTMLVideoElement>(null);
  const clock = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    v.muted = true;
    const mq = matchMedia('(prefers-reduced-motion: reduce)');
    let visible = true, raf = 0, shown = '';
    const sync = () => { if (visible && !mq.matches) v.play().catch(() => undefined); else v.pause(); };
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; sync(); });
    io.observe(v);
    mq.addEventListener('change', sync);
    const tick = () => {
      raf = requestAnimationFrame(tick);
      if (v.paused || !v.duration || !clock.current) return;
      const m = HERO_DAY[0] + (v.currentTime / v.duration) * (HERO_DAY[1] - HERO_DAY[0]);
      const s = `21. 6. · ${hhmm(Math.round(m / 5) * 5)}`;
      if (s !== shown) clock.current.textContent = shown = s;
    };
    tick();
    return () => { cancelAnimationFrame(raf); io.disconnect(); mq.removeEventListener('change', sync); };
  }, []);
  return (
    <>
      <video ref={ref} width={960} height={720} poster="/demo/hero.jpg" muted loop playsInline preload="auto" aria-hidden="true">
        <source src="/demo/hero.webm" type="video/webm" />
        <source src="/demo/hero.mp4" type="video/mp4" />
      </video>
      <span class="hero-clock num" aria-hidden="true" ref={clock}>21. 6. · {hhmm(HERO_POSTER)}</span>
    </>
  );
}

export function HeroVisual() {
  const pv = D.facts.find((f) => f.unit === 'kWp');
  const ground = D.facts[1];
  const H = t.site.hero;
  const parcel = D.title.replace(/^Parcela\s*/, '');
  return (
    <figure class="hero-visual">
      <HeroArcs />
      <a href={D.href} class="hero-img" onClick={() => track('demo_click', { from: 'hero' })} aria-label={`${t.site.demo.open}: ${D.title}`}>
        <HeroVideo />
      </a>
      <figcaption class="titleblock">
        <span>{H.sheet.parcel}<b>{parcel}</b></span>
        <span>{H.sheet.layer}<b>{H.sheet.layerVal}</b></span>
        <span>{H.sheet.ramp}<i class="ramp" style={{ background: gradientCss(RAMP_ANNUAL) }} /></span>
      </figcaption>
      {pv && <span class="chip chip-a"><b class="num">{pv.v} {pv.unit}</b>{H.chipPv}</span>}
      {ground && <span class="chip chip-b"><b class="num">{ground.v} {ground.unit}</b>{H.chipGround}</span>}
    </figure>
  );
}

function SectionHead({ eyebrow, title, lead, id }: { eyebrow: string; title: string; lead?: string; id: string }) {
  return (
    <header class="sec-head">
      <span class="eyebrow">{eyebrow}</span>
      <h2 id={id}>{title}</h2>
      {lead && <p class="lead">{lead}</p>}
    </header>
  );
}

export function HomeSections() {
  const S = t.site;
  return (
    <>
      <section id="primer" class="band demo" aria-labelledby="demo-title">
        <SectionHead id="demo-title" eyebrow={S.demo.eyebrow} title={S.demo.title} lead={S.demo.lead} />
        <div class="facts">
          {D.facts.map((f, i) => <div class="fact" key={i}><div class="v num">{f.v}<small>{f.unit}</small></div><div class="l">{f.label}</div></div>)}
        </div>
        <div class="demo-grid">
          <figure>
            <img src={D.images.map.src} width={D.images.map.w} height={D.images.map.h} loading="lazy" decoding="async" alt={S.hero.visualAlt} />
            <figcaption class="cap">{S.demo.mapCap}</figcaption>
          </figure>
          <figure>
            <img src={D.images.three.src} width={D.images.three.w} height={D.images.three.h} loading="lazy" decoding="async" alt={S.demo.threeCap} />
            <figcaption class="cap">{S.demo.threeCap}</figcaption>
          </figure>
        </div>
        <ul class="find">{D.findings.map((s, i) => <li key={i}>{s}</li>)}</ul>
        <div class="demo-actions">
          <a class="btn primary" href={D.href} onClick={() => track('demo_click', { from: 'demo' })}>{S.demo.open} →</a>
          {D.more.length > 0 && (
            <div class="more">
              <span class="eyebrow">{S.demo.more}</span>
              <ul>{D.more.map((m) => <li key={m.href}><a href={m.href}>{m.label}</a> <span class="cap">{m.note}</span></li>)}</ul>
            </div>
          )}
        </div>
      </section>

      <section id="za-koga" class="band" aria-labelledby="aud-title">
        <SectionHead id="aud-title" eyebrow={S.audience.eyebrow} title={S.audience.title} lead={S.audience.lead} />
        <ul class="cards aud">
          {S.audience.items.map((a, i) => (
            <li key={a.tag} class="card">
              <svg class="ico" viewBox="0 0 24 24" aria-hidden="true"><path d={AUD_ICONS[i]} /></svg>
              <span class="tag">{a.tag}</span>
              <h3>{a.h}</h3>
              <p>{a.p}</p>
              <ul>{a.points.map((x) => <li key={x}>{x}</li>)}</ul>
            </li>
          ))}
        </ul>
      </section>

      <section id="kaj-dobite" class="band warm" aria-labelledby="feat-title">
        <SectionHead id="feat-title" eyebrow={S.features.eyebrow} title={S.features.title} />
        <ul class="cards">
          {S.features.items.map((f, i) => (
            <li key={f.h} class="card">
              <svg class="ico" viewBox="0 0 24 24" aria-hidden="true"><path d={ICONS[i]} /></svg>
              <h3>{f.h}</h3>
              <p>{f.p}</p>
            </li>
          ))}
        </ul>
      </section>

      <section id="kako-deluje" class="band" aria-labelledby="how-title">
        <SectionHead id="how-title" eyebrow={S.how.eyebrow} title={S.how.title} />
        <ol class="steps-h">
          {S.how.steps.map((s, i) => <li key={s.h}><span class="n num" aria-hidden="true">{i + 1}</span><h3>{s.h}</h3><p>{s.p}</p></li>)}
        </ol>
      </section>

      <section id="zakaj" class="band warm" aria-labelledby="data-title">
        <SectionHead id="data-title" eyebrow={S.data.eyebrow} title={S.data.title} />
        <dl class="stats">
          {S.data.stats.map((s) => <div key={s.l}><dt class="num">{s.v}</dt><dd>{s.l}</dd></div>)}
        </dl>
      </section>

      <section id="vprasanja" class="band" aria-labelledby="faq-title">
        <SectionHead id="faq-title" eyebrow={S.faq.eyebrow} title={S.faq.title} />
        <div class="faq">
          {S.faq.items.map((f) => <details key={f.q}><summary>{f.q}</summary><p>{f.a}</p></details>)}
        </div>
      </section>

      {leadEnabled && (
        <section id="cakalni-seznam" class="band waitband" aria-labelledby="wait-title">
          <div class="waitcard">
            <div>
              <h2 id="wait-title">{S.waitlist.title}</h2>
              <p class="lead">{S.waitlist.lead}</p>
            </div>
            <WaitlistForm region="" ko={0} parcel="" askObcina />
          </div>
        </section>
      )}

      <section class="final" aria-labelledby="final-title">
        <h2 id="final-title">{S.final.title}</h2>
        <p class="lead">{S.final.lead}</p>
        <a class="btn primary big" href="#iskanje" onClick={focusSearch}>{S.final.button}</a>
      </section>
    </>
  );
}

/** Scroll to the search card and focus its first input. */
export function focusSearch(e?: Event) {
  const card = document.getElementById('iskanje');
  if (!card) return;
  e?.preventDefault();
  card.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
  card.querySelector<HTMLInputElement>('input')?.focus({ preventScroll: true });
}

export const demoData = D;
